import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Grid,
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Work as WorkIcon,
  Checkroom as CheckroomIcon,
  School as SchoolIcon,
  MenuBook as MenuBookIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

type EmploymentType = "Full time" | "intern" | "Pixel Pilot" | "Part time" | "contract";

interface EmploymentHistory {
  id: number;
  position: string;
  department: string;
  employment_type: EmploymentType;
  start_date: string;
  end_date: string | null;
  is_current: boolean;
}

interface Course {
  id: number;
  course_name: string;
  institution: string;
  start_date: string;
  end_date: string | null;
  status: "In Progress" | "Completed" | "Dropped";
}

type TShirtSize = "XS" | "S" | "M" | "L" | "XL" | "XXL" | "XXXL";
type ClothingSize = "XS" | "S" | "M" | "L" | "XL" | "XXL" | "XXXL";

interface ClothingSizes {
  shirt: ClothingSize;
  trousers: ClothingSize;
  shoe: number;
  jacket: ClothingSize;
}

interface ProfessionalDevelopmentTabProps {
  employmentHistory: EmploymentHistory[];
  setEmploymentHistory: React.Dispatch<React.SetStateAction<EmploymentHistory[]>>;
  courses: Course[];
  setCourses: React.Dispatch<React.SetStateAction<Course[]>>;
  tShirtSize: TShirtSize;
  setTShirtSize: React.Dispatch<React.SetStateAction<TShirtSize>>;
  clothingSizes: ClothingSizes;
  setClothingSizes: React.Dispatch<React.SetStateAction<ClothingSizes>>;
}

export default function ProfessionalDevelopmentTab({
  employmentHistory,
  setEmploymentHistory,
  courses,
  setCourses,
  tShirtSize,
  setTShirtSize,
  clothingSizes,
  setClothingSizes,
}: ProfessionalDevelopmentTabProps) {
  const [tShirtDialogOpen, setTShirtDialogOpen] = useState(false);
  const [workHistoryDialogOpen, setWorkHistoryDialogOpen] = useState(false);
  const [coursesDialogOpen, setCoursesDialogOpen] = useState(false);
  const [editingEmploymentId, setEditingEmploymentId] = useState<number | null>(null);
  const [newEmployment, setNewEmployment] = useState<Partial<EmploymentHistory>>({
    position: "",
    department: "",
    employment_type: "Full time",
    start_date: "",
    end_date: null,
    is_current: false,
  });
  const [newCourse, setNewCourse] = useState<Partial<Course>>({
    course_name: "",
    institution: "",
    start_date: "",
    end_date: null,
    status: "In Progress",
  });

  const handleAddEmployment = () => {
    if (newEmployment.position && newEmployment.start_date) {
      setEmploymentHistory([
        ...employmentHistory,
        {
          id: Date.now(),
          position: newEmployment.position,
          department: newEmployment.department || "",
          employment_type: newEmployment.employment_type as EmploymentType,
          start_date: newEmployment.start_date,
          end_date: newEmployment.end_date,
          is_current: newEmployment.is_current,
        } as EmploymentHistory,
      ]);
      setNewEmployment({
        position: "",
        department: "",
        employment_type: "Full time",
        start_date: "",
        end_date: null,
        is_current: false,
      });
    }
  };

  const handleUpdateEmployment = (id: number, data: Partial<EmploymentHistory>) => {
    setEmploymentHistory(
      employmentHistory.map((item) => (item.id === id ? { ...item, ...data } : item)),
    );
    setEditingEmploymentId(null);
  };

  const handleDeleteEmployment = (id: number) => {
    setEmploymentHistory(employmentHistory.filter((item) => item.id !== id));
  };

  const handleAddCourse = () => {
    if (newCourse.course_name && newCourse.institution && newCourse.start_date) {
      setCourses([
        ...courses,
        {
          id: Date.now(),
          course_name: newCourse.course_name,
          institution: newCourse.institution,
          start_date: newCourse.start_date,
          end_date: newCourse.end_date,
          status: newCourse.status as Course["status"],
        } as Course,
      ]);
      setNewCourse({
        course_name: "",
        institution: "",
        start_date: "",
        end_date: null,
        status: "In Progress",
      });
    }
  };

  const handleDeleteCourse = (id: number) => {
    setCourses(courses.filter((item) => item.id !== id));
  };

  const calculateDuration = (startDate: string, endDate: string | null, isCurrent: boolean) => {
    const start = new Date(startDate);
    const end = isCurrent ? new Date() : endDate ? new Date(endDate) : new Date();
    const duration = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    const years = Math.floor(duration / 365);
    const months = Math.floor((duration % 365) / 30);
    return years > 0 ? `${years}y ${months}m` : `${months}m`;
  };

  return (
    <Box sx={{ mt: 1, ml: 2, mr: 2, py: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Employee History
        </Typography>
      </Box>

      {/* Work History Section */}
      <Card sx={{ boxShadow: 0, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <WorkIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Work History
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setWorkHistoryDialogOpen(true)}
              >
                Add Position
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Position</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Employment Type</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employmentHistory.map((emp) => (
                  <TableRow key={emp.id}>
                    <TableCell>{emp.position}</TableCell>
                    <TableCell>{emp.department || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={emp.employment_type}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{format(new Date(emp.start_date), "dd MMM yyyy")}</TableCell>
                    <TableCell>
                      {emp.is_current ? (
                        <Chip label="Present" color="success" size="small" />
                      ) : emp.end_date ? (
                        format(new Date(emp.end_date), "dd MMM yyyy")
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {calculateDuration(emp.start_date, emp.end_date, emp.is_current)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={emp.is_current ? "Current" : "Previous"}
                        color={emp.is_current ? "primary" : "default"}
                        size="small"
                        variant={emp.is_current ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingEmploymentId(emp.id);
                            setNewEmployment(emp);
                            setWorkHistoryDialogOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEmployment(emp.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Courses Section */}
      <Card sx={{ boxShadow: 0, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <SchoolIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Courses & Certifications
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCoursesDialogOpen(true)}
            >
              Add Course
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Course</TableCell>
                  <TableCell>Institution</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {courses.map((course) => (
                  <TableRow key={course.id}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <MenuBookIcon fontSize="small" color="disabled" />
                        {course.course_name}
                      </Box>
                    </TableCell>
                    <TableCell>{course.institution}</TableCell>
                    <TableCell>{format(new Date(course.start_date), "dd MMM yyyy")}</TableCell>
                    <TableCell>
                      {course.end_date ? format(new Date(course.end_date), "dd MMM yyyy") : "-"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={course.status}
                        color={
                          course.status === "Completed"
                            ? "success"
                            : course.status === "In Progress"
                              ? "primary"
                              : "error"
                        }
                        size="small"
                        variant={course.status === "Completed" ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCourse(course.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Clothing Section */}
      <Card sx={{ boxShadow: 0 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CheckroomIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Clothing
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setTShirtDialogOpen(true)}
            >
              Edit
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  T-Shirt:
                </Typography>
                <Chip
                  label={tShirtSize}
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{ fontSize: "1rem", px: 2, py: 1 }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Shirt:
                </Typography>
                <Chip
                  label={clothingSizes.shirt}
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{ fontSize: "1rem", px: 2, py: 1 }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Trousers:
                </Typography>
                <Chip
                  label={clothingSizes.trousers}
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{ fontSize: "1rem", px: 2, py: 1 }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Shoe:
                </Typography>
                <Chip
                  label={clothingSizes.shoe}
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{ fontSize: "1rem", px: 2, py: 1 }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Jacket:
                </Typography>
                <Chip
                  label={clothingSizes.jacket}
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{ fontSize: "1rem", px: 2, py: 1 }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Clothing Size Dialog */}
      <Dialog open={tShirtDialogOpen} onClose={() => setTShirtDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Clothing Sizes</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>T-Shirt Size</InputLabel>
                <Select
                  value={tShirtSize}
                  label="T-Shirt Size"
                  onChange={(e) => setTShirtSize(e.target.value as TShirtSize)}
                >
                  <MenuItem value="XS">XS</MenuItem>
                  <MenuItem value="S">S</MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="L">L</MenuItem>
                  <MenuItem value="XL">XL</MenuItem>
                  <MenuItem value="XXL">XXL</MenuItem>
                  <MenuItem value="XXXL">XXXL</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Shirt Size</InputLabel>
                <Select
                  value={clothingSizes.shirt}
                  label="Shirt Size"
                  onChange={(e) => setClothingSizes({ ...clothingSizes, shirt: e.target.value as ClothingSize })}
                >
                  <MenuItem value="XS">XS</MenuItem>
                  <MenuItem value="S">S</MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="L">L</MenuItem>
                  <MenuItem value="XL">XL</MenuItem>
                  <MenuItem value="XXL">XXL</MenuItem>
                  <MenuItem value="XXXL">XXXL</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Trousers Size</InputLabel>
                <Select
                  value={clothingSizes.trousers}
                  label="Trousers Size"
                  onChange={(e) => setClothingSizes({ ...clothingSizes, trousers: e.target.value as ClothingSize })}
                >
                  <MenuItem value="XS">XS</MenuItem>
                  <MenuItem value="S">S</MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="L">L</MenuItem>
                  <MenuItem value="XL">XL</MenuItem>
                  <MenuItem value="XXL">XXL</MenuItem>
                  <MenuItem value="XXXL">XXXL</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Shoe Size"
                type="number"
                size="small"
                value={clothingSizes.shoe}
                onChange={(e) => setClothingSizes({ ...clothingSizes, shoe: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 30, max: 50 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Jacket Size</InputLabel>
                <Select
                  value={clothingSizes.jacket}
                  label="Jacket Size"
                  onChange={(e) => setClothingSizes({ ...clothingSizes, jacket: e.target.value as ClothingSize })}
                >
                  <MenuItem value="XS">XS</MenuItem>
                  <MenuItem value="S">S</MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="L">L</MenuItem>
                  <MenuItem value="XL">XL</MenuItem>
                  <MenuItem value="XXL">XXL</MenuItem>
                  <MenuItem value="XXXL">XXXL</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTShirtDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => setTShirtDialogOpen(false)} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Work History Dialog */}
      <Dialog
        open={workHistoryDialogOpen}
        onClose={() => {
          setWorkHistoryDialogOpen(false);
          setEditingEmploymentId(null);
          setNewEmployment({ position: "", employment_type: "Full time", start_date: "", end_date: null, is_current: false });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingEmploymentId ? "Edit Position" : "Add New Position"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Position"
                size="small"
                value={newEmployment.position}
                onChange={(e) => setNewEmployment({ ...newEmployment, position: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Department"
                size="small"
                value={newEmployment.department || ""}
                onChange={(e) => setNewEmployment({ ...newEmployment, department: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Employment Type</InputLabel>
                <Select
                  value={newEmployment.employment_type}
                  label="Employment Type"
                  onChange={(e) => setNewEmployment({ ...newEmployment, employment_type: e.target.value as EmploymentType })}
                >
                  <MenuItem value="Full time">Full time</MenuItem>
                  <MenuItem value="intern">Intern</MenuItem>
                  <MenuItem value="Pixel Pilot">Pixel Pilot</MenuItem>
                  <MenuItem value="Part time">Part time</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={newEmployment.start_date}
                onChange={(e) => setNewEmployment({ ...newEmployment, start_date: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                disabled={newEmployment.is_current}
                value={newEmployment.end_date || ""}
                onChange={(e) => setNewEmployment({ ...newEmployment, end_date: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  checked={newEmployment.is_current}
                  onChange={(e) =>
                    setNewEmployment({
                      ...newEmployment,
                      is_current: e.target.checked,
                      end_date: e.target.checked ? null : newEmployment.end_date,
                    })
                  }
                />
                <Typography variant="body2">Current Position</Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setWorkHistoryDialogOpen(false);
              setEditingEmploymentId(null);
              setNewEmployment({ position: "", department: "", employment_type: "Full time", start_date: "", end_date: null, is_current: false });
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (editingEmploymentId) {
                handleUpdateEmployment(editingEmploymentId, newEmployment);
              } else {
                handleAddEmployment();
              }
              setWorkHistoryDialogOpen(false);
              setEditingEmploymentId(null);
              setNewEmployment({ position: "", department: "", employment_type: "Full time", start_date: "", end_date: null, is_current: false });
            }}
            variant="contained"
          >
            {editingEmploymentId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Courses Dialog */}
      <Dialog
        open={coursesDialogOpen}
        onClose={() => {
          setCoursesDialogOpen(false);
          setNewCourse({ course_name: "", institution: "", start_date: "", end_date: null, status: "In Progress" });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Course</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Course Name"
                size="small"
                value={newCourse.course_name}
                onChange={(e) => setNewCourse({ ...newCourse, course_name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Institution"
                size="small"
                value={newCourse.institution}
                onChange={(e) => setNewCourse({ ...newCourse, institution: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={newCourse.start_date}
                onChange={(e) => setNewCourse({ ...newCourse, start_date: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={newCourse.end_date || ""}
                onChange={(e) => setNewCourse({ ...newCourse, end_date: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={newCourse.status}
                  label="Status"
                  onChange={(e) => setNewCourse({ ...newCourse, status: e.target.value as Course["status"] })}
                >
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Dropped">Dropped</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCoursesDialogOpen(false);
              setNewCourse({ course_name: "", institution: "", start_date: "", end_date: null, status: "In Progress" });
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleAddCourse();
              setCoursesDialogOpen(false);
              setNewCourse({ course_name: "", institution: "", start_date: "", end_date: null, status: "In Progress" });
            }}
            variant="contained"
          >
            Add Course
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
